import React, { useEffect, useState, useRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import ArrowUpIcon from "assets/icons/ArrowUpIcon";
import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import type { DropdownInputOption } from "types/DropdownInputOption";

interface DropdownInputProps {
  placeholder: string;
  options: DropdownInputOption[];
  value: string;
  disable?: boolean;
  isFullHeight?: boolean;
  variant?: "default" | "header";
  prefix?: ReactNode;
  onChange: (value: string) => void;
  onOpen?: () => void;
  className?: string;
  dropdownClassName?: string;
}

const DropdownInput: React.FC<DropdownInputProps> = ({
  placeholder,
  options,
  value,
  disable,
  isFullHeight = false,
  variant = "default",
  prefix,
  onChange,
  onOpen,
  className = "",
  dropdownClassName = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const valueElement = document.getElementById(`value_${value}`);
      valueElement?.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full cursor-pointer ${isFullHeight ? "h-full" : ""} ${className || ""} ${disable ? "bg-white/50 text-[#666666]" : ""}`}
    >
      <div
        className={`w-full flex items-stretch overflow-hidden ${
          variant === "header"
            ? "rounded-md border-0 bg-transparent transition-colors duration-200 hover:bg-[#F5F5F5]"
            : "rounded-lg border border-[#9B9B9B] bg-white"
        } ${
          variant === "header"
            ? "min-h-0"
            : isFullHeight
              ? "h-[46px] min-h-[46px]"
              : "h-[46px] min-h-[46px]"
        }`}
        onClick={() => {
          if (!disable) {
            setIsOpen(!isOpen);
            !isOpen && onOpen && onOpen();
          }
        }}
      >
        <div
          className={`flex min-w-0 flex-1 items-center ${
            variant === "header" ? "gap-2 px-1 py-1.5" : "px-3"
          }`}
        >
          {prefix ? <span className="shrink-0">{prefix}</span> : null}
          <p
            className={`truncate overflow-hidden text-ellipsis ${
              variant === "header"
                ? "text-sm font-medium text-black"
                : value
                  ? "text-base text-[#333333]"
                  : "text-base text-[#9ca3af]"
            }`}
          >
            {value
              ? t(
                  options.find((option) => option.value === value)?.label ||
                    placeholder,
                )
              : placeholder}
          </p>
        </div>
        <div
          className={`flex shrink-0 items-center justify-center ${
            variant === "header"
              ? "ml-1 w-7 self-stretch gap-1 bg-transparent"
              : "w-12 min-w-[48px] self-stretch bg-white"
          }`}
        >
          <span
            aria-hidden="true"
            className={`w-px bg-[#D9D9D9] ${
              variant === "header" ? "h-5" : "h-full" 
            }`}
          />
          {isOpen ? (
            <ArrowUpIcon
              width={variant === "header" ? 18 : 24}
              height={variant === "header" ? 18 : 24}
              color={variant === "header" ? "#000000" : "#767676"}
            />
          ) : (
            <ArrowDownIcon
              width={variant === "header" ? 18 : 24}
              height={variant === "header" ? 18 : 24}
              color={variant === "header" ? "#000000" : "#767676"}
            />
          )}
        </div>
      </div>
      {isOpen && (
        <ul
          className={`absolute z-40 list-none overflow-auto bg-white ${
            variant === "header"
              ? "right-0 min-w-[140px] rounded-xl border border-[#D9D9D9] p-2 shadow-[0_12px_24px_rgba(0,0,0,0.12)]"
              : "w-full max-h-[400px] rounded-lg border border-[#9B9B9B] p-2 shadow-lg"
          } ${dropdownClassName || ""}`}
        >
          {options.map((option) => (
            <li
              key={option.value}
              id={`value_${option.value}`}
              className={`cursor-pointer rounded-md ${
                variant === "header" ? "px-3 py-2 text-sm" : "p-2"
              } ${option.value === value ? "bg-[#F4F4F4]" : ""} hover:bg-[#F4F4F4]`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {t(option.label)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DropdownInput;
